import React, { useReducer, useMemo, useEffect, useCallback, useState, useRef } from 'react'
import { navigate } from 'gatsby'
import CustomForm from './CustomForm'
import { useDomainChecker } from '../../utils/hooks/useDomainChecker'
import { startOauthFlow } from '../../utils/oauth-init'
import { ACCESS_STATUS_INSTITUTIONAL, hasInstitutionalGroup, useAuth } from '../../utils/context/AuthContext'
import { buildConfirmEmailPath } from '../../utils/auth-redirect'
import useGetQueryString from '../../utils/hooks/useQueryString'
import { assertNoAuthErrorPayload, extractAuthErrorMessage } from '../../utils/auth-errors'
import { ReactComponent as HarborLogo } from '../../images/HFLogoOnlyDark.svg'
import { ReactComponent as GoogleLogo } from '../../assets/images/icons/icons8-google.svg'
import { ReactComponent as AppleLogo } from '../../assets/images/logos/Apple_logo_black.svg'
import { ReactComponent as LinkedInLogo } from '../../assets/images/logos/LinkedIn_icon.svg'
import * as styles from '../../styles/components/cognito-register-form.module.scss'
import { useAnalytics } from '../../utils/analytics/useAnalytics'
import { setLoginMethod } from '../../utils/analytics/dataLayer'
import {
  clearStoredConfirmEmailState,
  writeStoredConfirmEmail
} from '../../utils/confirm-email-storage'
import { clearInstitutionalAccessRetry, storeInstitutionalAccessRetry } from '../../utils/institutional-access-retry'
import {
  clearRegistrationAuthError,
  clearRegistrationComplete,
  clearRegistrationHandoff,
  clearRegistrationProgress,
  markRegistrationComplete,
  readRegistrationEmail,
  readRegistrationState,
  storeRegistrationEmail,
  storeRegistrationFormError,
  storeRegistrationProgress,
  storeRegistrationUserId
} from '../../utils/registration-state'

const STAGE = { SIGNUP: 'signup', CONFIRM: 'confirm', FINAL_THANKS: 'final_thanks' }
// A grant the server just made is not on the token this sign-in issued. Bounded so a
// user who genuinely has no access is not held on the spinner for long.
const ACCESS_RECHECK_ATTEMPTS = 3
const ACCESS_RECHECK_DELAY_MS = 1000
const isBrowser = typeof window !== 'undefined'

const wait = (ms) => new Promise((resolve) => { setTimeout(resolve, ms) })

const PENDING_REVIEW_NOTICE = 'Your business email is confirmed. Your institutional access request is being reviewed, and we will email you once it is approved.'
const GRANT_PENDING_TOKEN_NOTICE = 'Your business email is confirmed and your institutional access has been approved. It will be active on your account shortly.'
const INSTITUTIONAL_READY_NOTICE = 'Institutional access is active on your account.'
const VERIFY_UNAVAILABLE_NOTICE = 'Your account is confirmed and you are signed in. We could not finish activating institutional access just now - we will complete it automatically the next time you sign in.'
const REJECTED_CODE_NOTICE = 'That link could not set your password. Registration also sends a separate account verification email, and only the password setup email can set a password. We have sent a fresh setup link - open the most recent email and try again.'
const REJECTED_CODE_RESEND_FAILED_NOTICE = 'That link could not set your password. Use "Resend Link" below to get a new password setup link, then open the most recent email.'

// Cognito renders the sign-up verification code and the password-setup code from the same
// message template, so their links are indistinguishable in the inbox. Handing the wrong
// one to ConfirmForgotPassword comes back as expired, so a link the user never used can
// look dead on arrival.
const REJECTED_CODE_NAMES = new Set(['ExpiredCodeException', 'CodeMismatchException'])

const isRejectedCodeError = (error) => {
  const name = error?.name || error?.code || ''
  if (REJECTED_CODE_NAMES.has(name)) return true
  return /invalid\s+(verification\s+)?code|expired/i.test(error?.message || '')
}

const buildTemporarySignupPassword = () => {
  let randomChunk = `${Date.now()}${Math.random().toString(36).slice(2, 10)}`
  if (isBrowser && window.crypto?.getRandomValues) {
    const bytes = new Uint8Array(12)
    window.crypto.getRandomValues(bytes)
    randomChunk = Array.from(bytes, (b) => (b % 36).toString(36)).join('')
  }
  return `Hca!9${randomChunk}Aa`
}

const initialState = {
  stage: STAGE.SIGNUP,
  form: { email: '', given_name: '', family_name: '' },
  confirm: { code: '', password: '' },
  userSub: '',
  done: false
}

function reducer(state, a) {
  switch (a.type) {
    case 'SET_STAGE': return { ...state, stage: a.stage }
    case 'FORM': return { ...state, form: a.data }
    case 'CONFIRM': return { ...state, confirm: a.data }
    case 'SET_CONFIRM_CODE': return { ...state, confirm: { ...state.confirm, code: a.code || '' } }
    case 'USER_SUB': return { ...state, userSub: a.sub }
    case 'DONE': return { ...state, done: true }
    case 'RESTORE':
      return {
        ...state,
        stage: a.stage || state.stage,
        done: a.complete === true ? true : state.done,
        form: {
          ...state.form,
          email: a.email || state.form.email,
        },
        userSub: a.userId || state.userSub,
      }
    default: return state
  }
}

function OrDivider({ children = 'OR SIGN UP WITH EMAIL' }) {
  return (
    <div className={styles.orDivider}>
      <span>{children}</span>
    </div>
  )
}

export default function CognitoRegisterForm({ onSuccess }) {
  const { getQueryParam } = useGetQueryString()
  const userStr = getQueryParam('user')
  const codeStr = getQueryParam('confirmation_code')

  const [state, dispatch] = useReducer(reducer, {
    ...initialState,
    // Browser session state is restored in an effect below so it cannot change the
    // first client render relative to Gatsby's static HTML.
    form: { ...initialState.form },
    confirm: { ...initialState.confirm, code: codeStr || '' },
    // The URL names the account after the query-string hook has hydrated.
    userSub: userStr || ''
  })

  const { signup, requestReset, confirmReset, login, refresh, registerVerify, loading, access } = useAuth()
  const { track } = useAnalytics({
    component: { name: 'CognitoRegisterForm', description: 'Registration form' },
  })
  const [showAccessReminder, setShowAccessReminder] = useState(false)
  const [formErrorMessage, setFormErrorMessage] = useState('')
  const [resendMessage, setResendMessage] = useState('')
  const [postConfirmNotice, setPostConfirmNotice] = useState('')
  // True only while the sign-in and access check that follow a confirmed password are still
  // running, so the closing panel does not offer a login link to someone about to be
  // signed in anyway.
  const [confirmBusy, setConfirmBusy] = useState(false)
  const [accessNotice, setAccessNotice] = useState('')
  const [resendEmail, setResendEmail] = useState('')
  const [signupInitialValues, setSignupInitialValues] = useState({ email: '', given_name: '', family_name: '' })
  const [confirmInitialValues, setConfirmInitialValues] = useState({ code: '', email: '', password: '' })
  const [hasAcknowledgedAccess, setHasAcknowledgedAccess] = useState(false)
  // Escape hatch only - the code is never shown to the user - for a mail client that
  // strips links or a code read off another device.
  const [showManualCode, setShowManualCode] = useState(false)
  const restoredProgressRef = useRef(false)
  // Kept in a ref so the effects below - both of which exist to put an unfinished
  // registration back on the confirm step - cannot drag a finished one there.
  const registerCompleteRef = useRef(false)
  // The server's domain verdict, carried to the confirm step so the sign-in that follows can
  // route on it instead of assuming a grant.
  const expectedInstitutionalRef = useRef(false)

  const domainState = useDomainChecker((state.form.email || '').trim())
  const resendDomainState = useDomainChecker((resendEmail || '').trim())

  useEffect(() => {
    if (registerCompleteRef.current) return
    if (codeStr) {
      if (userStr) {
        dispatch({ type: 'USER_SUB', sub: userStr })
        storeRegistrationUserId(userStr)
      }
      dispatch({ type: 'SET_CONFIRM_CODE', code: codeStr })
      dispatch({ type: 'SET_STAGE', stage: STAGE.CONFIRM })
    }
  }, [userStr, codeStr])

  // Restored on every arrival, including the one the emailed link starts - precisely the
  // load where nothing is in memory, so bailing out on a URL code dropped the server's
  // domain verdict and stopped the confirm step finishing institutional access.
  useEffect(() => {
    if (restoredProgressRef.current) return
    restoredProgressRef.current = true
    const restored = readRegistrationState()
    const restoredEmail = restored.email.trim()
    const restoredStage = restored.complete
      ? STAGE.FINAL_THANKS
      : restored.startedHere && !codeStr
        ? STAGE.CONFIRM
        : undefined

    if (restored.complete) registerCompleteRef.current = true
    if (restored.expectedInstitutional) expectedInstitutionalRef.current = true

    if (restoredEmail) {
      setSignupInitialValues((values) => ({ ...values, email: restoredEmail }))
      setConfirmInitialValues((values) => ({ ...values, email: restoredEmail }))
      setResendEmail(restoredEmail)
      storeRegistrationEmail(restoredEmail)
    }

    if (restored.formError) setFormErrorMessage(restored.formError)

    if (restoredEmail || restored.userId || restored.complete || restoredStage) {
      dispatch({
        type: 'RESTORE',
        stage: restoredStage,
        complete: restored.complete,
        email: restoredEmail,
        userId: userStr || restored.userId,
      })
      if (restored.userId && !userStr) storeRegistrationUserId(restored.userId)
    }
  }, [codeStr, userStr])

  useEffect(() => {
    clearRegistrationAuthError()
  }, [])

  const domainBannerText = useMemo(() => {
    if (domainState === 'checking') return 'Checking email domain…'
    if (domainState === 'valid') return 'Business email detected.'
    if (domainState === 'invalid') return 'Institutional access requires a business email; you can verify one later.'
    if (domainState === 'error') return 'We could not check your email domain just now. You can continue - we will verify it when your request is reviewed.'
    return ''
  }, [domainState])

  const resendBannerText = useMemo(() => {
    if (!resendEmail) return ''
    if (resendDomainState === 'checking') return 'Checking email domain…'
    if (resendDomainState === 'valid') return 'Business email detected.'
    if (resendDomainState === 'invalid') return 'Institutional access requires a business email; you may verify later.'
    if (resendDomainState === 'error') return 'We could not check your email domain just now.'
    return ''
  }, [resendDomainState, resendEmail])

  const handleEmailInput = useCallback((e) => {
    storeRegistrationEmail(e.target.value)
    if (formErrorMessage) {
      setFormErrorMessage('')
      clearRegistrationAuthError()
    }
  }, [formErrorMessage])

  const emailFields = useMemo(
    () => [
      { type: 'input', props: { id: 'email', name: 'email', label: 'Email', type: 'email', required: true, autoComplete: 'email', freevalidation: 'false', helperText: domainState === 'invalid' ? 'Institutional access requires a business email; you may verify later.' : '', onInput: handleEmailInput } },
      { type: 'input', props: { id: 'given_name', name: 'given_name', label: 'First name', group: 'name', required: true } },
      { type: 'input', props: { id: 'family_name', name: 'family_name', label: 'Last name', group: 'name', required: true } }
    ],
    [domainState, handleEmailInput]
  )

  // The setup e-mail sends a link, not a code to retype: it returns here with
  // ?confirmation_code=... already in the URL. Keyed off the URL param rather than
  // state.confirm.code, so typing into the manual field cannot pull it out from under the
  // user.
  const hasLinkCode = Boolean(codeStr)
  const showCodeField = !hasLinkCode && showManualCode
  // Nothing to submit yet - the code has to arrive from the inbox first.
  const awaitingLink = state.stage === STAGE.CONFIRM && !hasLinkCode && !showManualCode
  const confirmTargetEmail = (resendEmail || state.form.email || '').trim()
  // The link is routinely opened on a phone, where this browser has never seen the
  // registration. Ask for the address rather than failing the submit on one the user has
  // no way to supply.
  const needsEmailField = state.stage === STAGE.CONFIRM && !confirmTargetEmail
  // Registration signs the user in as its last step, so the closing panel speaks to
  // someone already through the door.
  const isSignedIn = Boolean(access?.isAuthenticated)

  const handleShowManualCode = useCallback(() => {
    setShowManualCode(true)
    setResendMessage('')
  }, [])

  const confirmFields = useMemo(
    () => [
      // Only reached when the address could not be recovered at all: the link was opened
      // on another device, where neither the cookie nor this browser's storage exists.
      ...(needsEmailField
        ? [
          {
            type: 'note',
            props: {
              id: 'register-email-reason',
              variant: 'body2',
              className: styles.passwordIntro,
              text: 'This link was opened on a different device or browser, so confirm the email address you registered with.'
            }
          },
          { type: 'input', props: { id: 'email', name: 'email', label: 'Email', type: 'email', required: true, autoComplete: 'email', freevalidation: 'false', ariaDescribedBy: 'register-email-reason' } }
        ]
        : []),
      ...(showCodeField
        ? [{ type: 'input', props: { id: 'code', name: 'code', label: 'Verification code', required: true, autoComplete: 'one-time-code', inputMode: 'numeric' } }]
        : []),
      {
        type: 'note',
        props: {
          id: 'register-password-guidance',
          variant: 'body2',
          className: styles.passwordIntro,
          text: hasLinkCode
            ? 'Your confirmation link has been applied. Set your password using the requirements below to finish registering.'
            : 'Set your password using the requirements below to complete registration.'
        }
      },
      {
        type: 'input',
        props: {
          id: 'password',
          name: 'password',
          label: 'Create your password',
          type: 'password',
          required: true,
          autoComplete: 'new-password',
          passwordPolicy: true,
          initial: true,
          helperText: 'Follow the requirements listed below.',
          ariaDescribedBy: 'register-password-guidance'
        }
      }
    ],
    [hasLinkCode, needsEmailField, showCodeField]
  )

  // Routed rather than rewritten with history.replaceState: the code is read off the
  // router location, so a history-only rewrite would leave the dead code in play.
  const dropLinkCodeFromUrl = useCallback(() => {
    if (!isBrowser) return
    const params = new URLSearchParams(window.location.search)
    params.delete('confirmation_code')
    const search = params.toString()
    navigate(`${window.location.pathname}${search ? `?${search}` : ''}`, { replace: true })
  }, [])

  // Puts a registration whose link carried the wrong code back on the "check your email"
  // step with a fresh link already sent, rather than leaving it on a form that can only
  // re-submit the code Cognito just refused.
  const recoverFromRejectedCode = useCallback(async (email) => {
    dropLinkCodeFromUrl()
    setShowManualCode(false)
    dispatch({ type: 'SET_CONFIRM_CODE', code: '' })
    // The link may have been opened where this browser never saw the registration, so
    // carry the resolved address into the resend control or it has nothing to send to.
    setResendEmail((prev) => prev || email)

    let notice = REJECTED_CODE_NOTICE
    try {
      const resetResponse = await requestReset(email)
      assertNoAuthErrorPayload(resetResponse, 'Unable to send a new password setup link right now.')
    } catch (resendError) {
      console.warn('Unable to resend the password setup link after a rejected code', resendError)
      notice = REJECTED_CODE_RESEND_FAILED_NOTICE
    }

    setResendMessage(notice)
    return { errorMessage: notice }
  }, [dispatch, dropLinkCodeFromUrl, requestReset])

  const handleResendCode = useCallback(async () => {
    try {
      const email = (resendEmail || '').trim()
      if (!email) {
        setResendMessage('Enter your email address to get a new link.')
        return
      }
      track('password_reset_request', {
        interaction: { type: 'request_code' },
      })
      storeRegistrationEmail(email)
      const resetResponse = await requestReset(email)
      assertNoAuthErrorPayload(resetResponse, 'Unable to send a new link right now.')
      setResendMessage('A new confirmation link has been sent to your email. Open it to continue.')
    } catch (error) {
      console.error('Error requesting new confirmation link', error)
      setResendMessage(extractAuthErrorMessage(error, 'Unable to send a new link right now.'))
    }
  }, [requestReset, resendEmail, track])

  const handleResendEmailChange = useCallback((event) => {
    const { value } = event.target
    setResendEmail(value)
    storeRegistrationEmail(value)
  }, [setResendEmail])

  const handleGateAcknowledgementChange = useCallback((event) => {
    const { checked } = event.target
    setHasAcknowledgedAccess(checked)
    if (checked) setShowAccessReminder(false)
    if (formErrorMessage) {
      setFormErrorMessage('')
      clearRegistrationAuthError()
    }
  }, [formErrorMessage, setShowAccessReminder])

  const guardAccess = useCallback((action) => {
    if (loading) return
    if (!hasAcknowledgedAccess) {
      setShowAccessReminder(true)
      return
    }
    setResendMessage('')
    return action?.()
  }, [hasAcknowledgedAccess, setShowAccessReminder, loading])

  const handleSignup = useCallback(async v => {
    try {
      setResendMessage('')
      setFormErrorMessage('')
      storeRegistrationFormError('')
      clearRegistrationAuthError()
      clearRegistrationProgress()
      if (!hasAcknowledgedAccess) {
        setShowAccessReminder(true)
        throw new Error('Please confirm your institutional access request before continuing.')
      }
      const loginMethod = 'email_password'
      setLoginMethod(loginMethod)
      track('sign_up_attempt', {
        interaction: { type: 'submit' },
        user: { loginMethod },
      })
      // Only the live check for the address being submitted may be sent as a hint: a
      // remembered verdict was computed for whatever address was typed before, and reusing
      // one once stamped a business-email marker onto a free-mail signup. 'checking' and
      // 'error' are states of the lookup, not answers, so they send nothing.
      const resolvedDomainStatus = (domainState === 'valid' || domainState === 'invalid') ? domainState : ''
      const trimmedEmail = (v.email || '').trim()
      storeRegistrationEmail(trimmedEmail)
      const tempPassword = buildTemporarySignupPassword()
      const r = await signup(trimmedEmail, v.given_name, v.family_name, resolvedDomainStatus || undefined, tempPassword)
      assertNoAuthErrorPayload(r, 'Unable to create your account right now. Please try again.')
      const createdUserId = r?.userSub || ''
      if (!createdUserId) throw new Error('Unable to complete registration without a user id.')

      // The server owns the domain verdict, whatever the browser assumed.
      expectedInstitutionalRef.current = Boolean(r?.expected_institutional)

      // Never imply access was granted when it was not: a filed request, a request that
      // could not be filed, and a domain the server did not recognise are three different
      // outcomes, and reporting all of them as silence is what left people believing they
      // had institutional access when nothing had been granted.
      setAccessNotice((() => {
        if (r?.access_error) {
          return 'Your account was created, but we could not finish your institutional access request. We will retry automatically the next time you sign in.'
        }
        if (r?.access_unconfirmed) {
          return 'Your account was created and your institutional access request was submitted, but we could not confirm it was granted. We will retry automatically when you sign in.'
        }
        if (r?.expected_institutional === false) {
          return 'Your account was created. We could not match your email to a recognised institutional domain, so your access request has been sent for review.'
        }
        return ''
      })())

      const resetResponse = await requestReset(trimmedEmail)
      assertNoAuthErrorPayload(resetResponse, 'Unable to request your password setup code right now. Please try again.')

      // Recorded only once the setup e-mail is on its way, so a reload can only be put back
      // on a confirm step that has a link to complete.
      storeRegistrationProgress({
        email: trimmedEmail,
        userId: createdUserId,
        expectedInstitutional: expectedInstitutionalRef.current
      })

      storeRegistrationFormError('')
      clearRegistrationAuthError()
      dispatch({ type: 'USER_SUB', sub: createdUserId })
      dispatch({ type: 'SET_STAGE', stage: STAGE.CONFIRM })
      track('sign_up_success', {
        interaction: { type: 'success' },
        user: { loginMethod },
      })
      return {}
    } catch (error) {
      const message = extractAuthErrorMessage(error, 'Unable to create your account right now. Please try again.')
      setFormErrorMessage(message)
      return { errorMessage: message }
    }
  }, [signup, requestReset, domainState, hasAcknowledgedAccess, setShowAccessReminder, dispatch, track])

  const handleConfirm = useCallback(async v => {
    setResendMessage('')
    setFormErrorMessage('')
    setPostConfirmNotice('')
    const resolvedEmail = [
      // Typed on this page wins: it is the only address available when the link was opened
      // somewhere this registration was never started.
      (v.email || '').trim(),
      (resendEmail || '').trim(),
      (state.form.email || '').trim(),
      // Last, and the only one of these that survives a link opened in a context this browser
      // session never saw - the cookie is one of the carriers it merges.
      readRegistrationState().email.trim()
    ].find(Boolean) || ''
    const code = v.code || state.confirm.code || codeStr || ''
    const password = v.password || state.confirm.password || ''
    if (!resolvedEmail) throw new Error('Email is required to confirm your account.')
    if (!password) throw new Error('Password is required.')
    if (resolvedEmail) storeRegistrationEmail(resolvedEmail)
    try {
      const r = await confirmReset(resolvedEmail, String(code || '').trim(), password)
      assertNoAuthErrorPayload(r, 'Unable to verify your account right now. Please try again.')
    } catch (error) {
      // A code the user never typed cannot have been mistyped, so a refusal here is almost
      // always the sign-up verification e-mail being opened instead of the password setup
      // one. A hand-typed code is left alone: there the message is the useful answer.
      if (hasLinkCode && isRejectedCodeError(error)) return recoverFromRejectedCode(resolvedEmail)
      throw error
    }
    clearRegistrationProgress()
    // Written before anything is awaited: the registration is finished whether or not the
    // sign-in and access check below succeed, so the record must be on disk if this
    // component is rebuilt while they run.
    markRegistrationComplete(resolvedEmail)
    registerCompleteRef.current = true
    dispatch({ type: 'DONE' })
    dispatch({ type: 'SET_STAGE', stage: STAGE.FINAL_THANKS })
    // The code is spent, so leaving it in the address bar lets a reload or a back button
    // re-enter the confirm step on a code that can only be refused.
    dropLinkCodeFromUrl()
    track('sign_up_confirm', {
      interaction: { type: 'confirm' },
    })

    setConfirmBusy(true)
    setPostConfirmNotice('Account confirmed. Signing you in...')

    try {
      let loggedInUser = await login(resolvedEmail, password)
      assertNoAuthErrorPayload(loggedInUser, 'Your account is confirmed. Please sign in to continue.')

      // The one call in the whole registration that grants access. Everything before it
      // files a Salesforce record and sets Cognito attributes; the institutional group -
      // the only thing the site routes on - is added here, the first point where both
      // things the grant requires exist: a session for this account, and an address the
      // code just consumed has proven.
      //
      // Deliberately not gated on the browser's remembered verdict, which is absent on the
      // load the emailed link starts. The endpoint re-derives the domain, checks the
      // address against the pool's own record, and answers confirm_email_required for
      // anything it cannot prove, so running it every time can never grant more than the
      // server proved.
      let verification = null
      let verifyFailed = false
      if (!hasInstitutionalGroup(loggedInUser) &&
          typeof registerVerify === 'function' &&
          loggedInUser?.sub) {
        setPostConfirmNotice('Account confirmed. Confirming your business email…')
        try {
          verification = await registerVerify(resolvedEmail, loggedInUser.sub)
        } catch (verifyError) {
          // Tracked separately from a `null` response: a 404 from a deploy without the
          // endpoint, a CORS rejection or a pre-flight throw must not read as "the server
          // says this address needs the confirm page".
          verifyFailed = true
          console.error('Business email verification after registration failed', {
            status: verifyError?.status ?? null,
            reason: verifyError?.body?.error || verifyError?.message || String(verifyError)
          })
        }
      }

      // The grant above landed after this sign-in issued its token, so pull a fresh one
      // before deciding where to send the user - otherwise someone who does have access gets
      // handed the confirm-your-business-email path. A verification that could not run still
      // counts as a reason to look, since registration already filed the request.
      const grantExpected = verification?.email_matched === true ||
        verification?.domain_status === 'valid' ||
        expectedInstitutionalRef.current ||
        verifyFailed
      if (grantExpected && typeof refresh === 'function') {
        for (let attempt = 0; attempt < ACCESS_RECHECK_ATTEMPTS; attempt += 1) {
          if (hasInstitutionalGroup(loggedInUser)) break
          // Refresh first, wait only before a further attempt: the grant is already made
          // by the time we get here, so the first fresh token usually carries it.
          if (attempt > 0) await wait(ACCESS_RECHECK_DELAY_MS)
          const refreshedUser = await refresh({ fallbackToCached: true }).catch(() => null)
          if (refreshedUser) loggedInUser = refreshedUser
        }
      }

      // The group on a fresh token is the only proof of access, so it decides whether
      // anything is left to do - registerVerify clears the marker on a merely reported grant.
      if (loggedInUser?.sub) {
        if (hasInstitutionalGroup(loggedInUser)) clearInstitutionalAccessRetry()
        else if (grantExpected) storeInstitutionalAccessRetry({ email: resolvedEmail, userId: loggedInUser.sub })
      }

      if (isBrowser) {
        // Deliberately not a redirect to the login page: they were signed in a few lines
        // ago, and a login form with no word about what happened reads as a failure.
        if (hasInstitutionalGroup(loggedInUser)) {
          setPostConfirmNotice(INSTITUTIONAL_READY_NOTICE)
          return {}
        }

        // The account exists, the address is proven and the request is filed, so this is a
        // delay, not a refusal, and the retry armed by registerVerify will finish it.
        if (verifyFailed) {
          setPostConfirmNotice(VERIFY_UNAVAILABLE_NOTICE)
          return {}
        }

        // The address is proven and the request is filed, so say so rather than sending them
        // to a page that asks for an address we just verified. A grant the token has not
        // caught up with is not the same as one waiting on review.
        if (verification?.email_matched) {
          // A boolean either way means the grant was attempted for a role the upstream called
          // institutional; only its absence means the upstream deferred.
          const grantLanded = typeof verification.access_group_ensured === 'boolean' ||
            (verification.access_status === ACCESS_STATUS_INSTITUTIONAL &&
              verification.access_deferred !== true &&
              verification.access_conflict !== true)
          setPostConfirmNotice(grantLanded ? GRANT_PENDING_TOKEN_NOTICE : PENDING_REVIEW_NOTICE)
          return {}
        }

        // That page exists for one case: an address the server does not recognise as
        // institutional, or one that is not the address on the account. Only a server verdict
        // sends anyone there, since otherwise it asks for an address already proven.
        const confirmEmailRequired = verification?.confirm_email_required === true ||
          (!verification && !expectedInstitutionalRef.current)
        if (confirmEmailRequired) {
          writeStoredConfirmEmail(resolvedEmail)
          navigate(buildConfirmEmailPath(loggedInUser?.sub, '/'), { replace: true })
          return {}
        }

        setPostConfirmNotice(PENDING_REVIEW_NOTICE)
        return {}
      }
    } catch (autoLoginError) {
      console.warn('Automatic login after registration confirmation failed', autoLoginError)
      setPostConfirmNotice('Account confirmed. Automatic sign in was unavailable. Use the login link below to continue.')
    } finally {
      // Cleared here rather than before the sign-in: everything above needs the address.
      setConfirmBusy(false)
      clearRegistrationHandoff()
    }
    return {}
  }, [confirmReset, state.confirm.code, state.confirm.password, codeStr, dispatch, dropLinkCodeFromUrl, hasLinkCode, recoverFromRejectedCode, resendEmail, state.form.email, login, refresh, registerVerify, track])

  const handleLinkedInClick = useCallback(() => guardAccess(() => {
    setLoginMethod('linkedin')
    track('oauth_login_start', {
      interaction: { type: 'click', text: 'Sign up with LinkedIn' },
      user: { loginMethod: 'linkedin' },
    })
    clearStoredConfirmEmailState()
    startOauthFlow('LinkedIn')
  }), [guardAccess, track])
  const handleGoogleClick = useCallback(() => guardAccess(() => {
    setLoginMethod('google')
    track('oauth_login_start', {
      interaction: { type: 'click', text: 'Sign up with Google' },
      user: { loginMethod: 'google' },
    })
    clearStoredConfirmEmailState()
    startOauthFlow('Google')
  }), [guardAccess, track])
  const handleAppleClick = useCallback(() => guardAccess(() => {
    setLoginMethod('apple')
    track('oauth_login_start', {
      interaction: { type: 'click', text: 'Sign up with Apple' },
      user: { loginMethod: 'apple' },
    })
    clearStoredConfirmEmailState()
    startOauthFlow('SignInWithApple')
  }), [guardAccess, track])
  const handleLoginClick = useCallback(() => {
    if (isBrowser) {
      track('login_start', {
        interaction: { type: 'click', text: 'Sign in', linkUrl: '/institutional/login/' },
      })
      window.location.href = '/institutional/login/'
    }
  }, [track])

  const handleContinueToSite = useCallback(() => {
    track('registration_complete_continue', {
      interaction: { type: 'click', text: 'Continue to Harbor Capital', linkUrl: '/' },
    })
    // Acknowledged, so a later visit in the same tab is a fresh start rather than a replay
    // of the panel they just dismissed.
    clearRegistrationComplete()
    registerCompleteRef.current = false
    navigate('/', { replace: true })
  }, [track])

  const onSignupValuesChange = useCallback((d) => {
    dispatch({ type: 'FORM', data: d })
  }, [dispatch])
  const onConfirmValuesChange = useCallback((d) => dispatch({ type: 'CONFIRM', data: d }), [])

  useEffect(() => {
    if (codeStr) setConfirmInitialValues((values) => ({ ...values, code: codeStr }))
  }, [codeStr])

  const initialValues = state.stage === STAGE.CONFIRM ? confirmInitialValues : signupInitialValues

  const cfg = useMemo(() => ({
    formId: state.stage === STAGE.CONFIRM ? 'cognito-confirm' : 'cognito-signup',
    fields: state.stage === STAGE.CONFIRM ? confirmFields : emailFields,
    button: state.stage === STAGE.CONFIRM ? 'Confirm and set password' : 'Sign up',
    onSubmit: state.stage === STAGE.CONFIRM ? handleConfirm : handleSignup,
    initialValues,
    onChange: state.stage === STAGE.CONFIRM ? onConfirmValuesChange : onSignupValuesChange
  }), [state.stage, confirmFields, emailFields, handleConfirm, handleSignup, initialValues, onConfirmValuesChange, onSignupValuesChange])

  const primaryButtonStyles = useMemo(() => {
    const gated = state.stage === STAGE.SIGNUP && !hasAcknowledgedAccess
    return {
      width: '100%',
      padding: '12px 16px',
      fontSize: '16px',
      fontWeight: '600',
      borderRadius: '16px',
      marginTop: '16px',
      marginBottom: '0px',
      cursor: gated ? 'not-allowed' : 'pointer',
      opacity: gated ? 0.6 : 1
    }
  }, [hasAcknowledgedAccess, state.stage])

  useEffect(() => {
    if (state.stage === STAGE.CONFIRM) {
      setResendEmail((prev) => prev || state.form.email || readRegistrationEmail())
    }
  }, [state.stage, state.form.email])

  useEffect(() => {
    storeRegistrationEmail(state.form.email)
  }, [state.form.email])

  useEffect(() => {
    storeRegistrationFormError(formErrorMessage)
  }, [formErrorMessage])

  return (
    <section className={styles.root} aria-busy={loading}>
      <div className={styles.header}>
        <HarborLogo title="Harbor Capital Logo" width={150} height={58} className={styles.logo} />
      </div>
      <div className={styles.formRow}>
        <div className={styles.panel}>
          {state.stage === STAGE.SIGNUP && (
            <>
              <div className={styles.registerDisclosure}>
                <p className={styles.disclosureTitle}><strong>Please review the following information if you are requesting Institutional access to the Harbor Capital&apos;s website:</strong></p>
                <p>THE INFORMATION ON THIS WEBSITE IS FOR INSTITUTIONAL INVESTOR USE ONLY AND IS NOT INTENDED FOR RETAIL DISTRIBUTION.</p>
                <p>By accessing this website, you certify that you are an Institutional Investor or are an agent acting on behalf of an Institutional Investor and are exercising independent judgment in your evaluation of any material on this website. &quot;Institutional Investors&quot; are banks, savings and loan associations, insurance companies, registered investment companies, registered investment advisers, government entities, employee benefit plans and qualified plans with at least 100 participants, FINRA members and registered persons, and any entity (including a natural person) with assets of at least $50 million.</p>
                <p>The information contained on this website is intended for use by Institutional Investors in the U.S. only. It is not intended for use by non-U.S. entities or for retail investors.</p>
                <p>The information contained on this website is current as of the date of issuance (or such earlier date as referenced herein) and is subject to change without notice. The information contained herein reflects information obtained from sources believed to be reliable but are not guaranteed as such.</p>
                <p>By accessing this website you agree that (i) your use of the website and the content contained herein is subject to the terms on this notice, the <a target="_blank" rel="noreferrer" href="https://assets.harborcapital.com/docs/HF_Terms_and_Conditions.pdf"><u>Terms of Use</u></a> in effect for the website, and the <a target="_blank" rel="noreferrer" href="https://assets.harborcapital.com/docs/Privacy_Policy.pdf"><u>Privacy Policy</u></a>; and (ii) you will not distribute material designated for Institutional Investor Use Only found on the website to persons who are not Institutional Investors or agents acting on behalf of Institutional Investors.</p>
              </div>
              <div className={styles.accessGate}>
                <label htmlFor="request_institutional_access" className={styles.accessCheckbox}>
                  <input
                    id="request_institutional_access"
                    name="request_institutional_access"
                    type="checkbox"
                    checked={hasAcknowledgedAccess}
                    onChange={handleGateAcknowledgementChange}
                  />
                  <span>Request Institutional Access</span>
                </label>
                <div className={styles.checkboxNote}>
                  <p className={styles.muted}>By clicking &quot;Request Institutional Access&quot;, you acknowledge that you are an Institutional Investor as defined above, the information you provided is accurate, and you accept and agree to the website terms of use.</p>
                </div>
                {(!hasAcknowledgedAccess || showAccessReminder) && (
                  <div
                    className={styles.accessReminder}
                    role={showAccessReminder ? 'alert' : 'status'}
                    aria-live="polite"
                  >
                    Select the checkbox above to unlock every sign up option.
                  </div>
                )}
              </div>
              <div className={styles.social}>
                <button
                  className={styles.btnOutlinedFull}
                  onClick={handleGoogleClick}
                  data-gated={!hasAcknowledgedAccess}
                  aria-disabled={!hasAcknowledgedAccess}
                  type="button"
                  disabled={loading || !hasAcknowledgedAccess}
                >
                  <GoogleLogo title="Google Logo" height={24} width={24} />
                  <span className={styles.btnLabelStrong}>Sign up with Google</span>
                </button>
                <button
                  className={styles.btnOutlinedFull}
                  onClick={handleLinkedInClick}
                  data-gated={!hasAcknowledgedAccess}
                  aria-disabled={!hasAcknowledgedAccess}
                  type="button"
                  disabled={loading || !hasAcknowledgedAccess}
                >
                  <LinkedInLogo title="LinkedIn Logo" height={24} width={24} />
                  <span className={styles.btnLabelStrong}>Sign up with LinkedIn</span>
                </button>
                <button
                  className={styles.btnOutlinedFull}
                  onClick={handleAppleClick}
                  data-gated={!hasAcknowledgedAccess}
                  aria-disabled={!hasAcknowledgedAccess}
                  type="button"
                  disabled={loading || !hasAcknowledgedAccess}
                >
                  <AppleLogo title="Apple Logo" height={24} width={24} />
                  <span className={styles.btnLabelStrong}>Sign up with Apple</span>
                </button>
              </div>
            </>
          )}
          {state.stage === STAGE.FINAL_THANKS && (
            <div className={styles.center}>
              <h3 className={styles.title}>{isSignedIn ? 'You’re all set!' : 'Account confirmed!'}</h3>
              <p className={styles.lead}>
                {isSignedIn
                  ? 'Your password is set, your account is verified, and you are signed in.'
                  : 'Your password is set and your account is verified.'}
              </p>
              {postConfirmNotice && (
                <div className={styles.statusInfo} role="status" aria-live="polite">
                  {confirmBusy && <span className={styles.spinner} aria-hidden="true" />}
                  <span>{postConfirmNotice}</span>
                </div>
              )}
              {isSignedIn && !confirmBusy && (
                <button
                  className={styles.btnFilledFull}
                  type="button"
                  onClick={handleContinueToSite}
                >
                  Continue to Harbor Capital
                </button>
              )}
            </div>
          )}
        </div>
        <div className={styles.panel}>
          {state.stage === STAGE.SIGNUP && <OrDivider />}
          {formErrorMessage && state.stage === STAGE.SIGNUP && (
            <div className={styles.statusInvalid} role="alert" aria-live="assertive">
              <span>{formErrorMessage}</span>
            </div>
          )}
          {/* Not on the closing panel, where postConfirmNotice reports what actually
              happened and a notice written back at the signup step would contradict it. */}
          {accessNotice && state.stage !== STAGE.FINAL_THANKS && (
            <div className={styles.statusInfo} role="status" aria-live="polite">
              <span>{accessNotice}</span>
            </div>
          )}
          {domainBannerText && state.stage === STAGE.SIGNUP && (
            <div className={
              domainState === 'checking' ? styles.statusChecking :
              domainState === 'valid' ? styles.statusValid :
              domainState === 'invalid' ? styles.statusInvalid : styles.statusInfo
            }>
              {domainState === 'checking' && <span className={styles.spinner} aria-hidden="true" />}
              <span>{domainBannerText}</span>
            </div>
          )}
          {awaitingLink && (
            <div className={styles.center}>
              <h3 className={styles.title}>Confirm your email</h3>
              <p className={styles.lead}>
                {confirmTargetEmail
                  ? `A confirmation email is on its way to ${confirmTargetEmail}. Click the link in that email to confirm this address and continue.`
                  : 'A confirmation email is on its way. Click the link in that email to confirm your address and continue.'}
              </p>
              <p className={styles.muted}>The link brings you back here with your confirmation already applied, so you can set your password and finish registering. It can take a minute to arrive - check your spam folder if you do not see it.</p>
              <p className={styles.muted}>Registration sends two emails: one verifying your account and one setting up your password. They look alike, so open the most recent one - only that link can set a password. If the link you open turns out to be the wrong one, we will send a fresh setup link automatically.</p>
              <button
                className={styles.btnText}
                type="button"
                onClick={handleShowManualCode}
              >
                I have a confirmation code to enter
              </button>
            </div>
          )}
          {(state.stage === STAGE.SIGNUP || (state.stage === STAGE.CONFIRM && !awaitingLink)) && (
            <div className={styles.emailForm}>
              <CustomForm
                key={cfg.formId}
                formId={cfg.formId}
                formFields={cfg.fields}
                initialValues={cfg.initialValues}
                ctaText={cfg.button}
                onSubmit={cfg.onSubmit}
                hideRecaptcha={true}
                onValuesChange={cfg.onChange}
                buttonStyles={primaryButtonStyles}
                submitDisabled={loading || (state.stage === STAGE.SIGNUP && !hasAcknowledgedAccess)}
                suppressErrorDisplay={Boolean(formErrorMessage)}
                requireCsrf={false}
              />
            </div>
          )}
          {state.done && !isSignedIn && !confirmBusy && (
            <div className={styles.afterLink}>
              <a href="/institutional/login/">Go to Login</a>
            </div>
          )}
        </div>
        {state.stage === STAGE.SIGNUP && (
          <div className={styles.panel}>
            <div className={styles.centerRow}>
              <p className={styles.muted}>Already registered?</p>
              <button className={styles.btnText} onClick={handleLoginClick}>Sign in</button>
            </div>
          </div>
        )}
        {state.stage === STAGE.CONFIRM && (
          <div className={styles.panel}>
            <div className={styles.center}>
              <div className={styles.requestCodeBox}>
                <label htmlFor="resend-email" className={styles.requestLabel}>Didn&apos;t get the email? Send the link again.</label>
                <div className={styles.requestCodeRow}>
                  <input
                    id="resend-email"
                    name="resend_email"
                    type="email"
                    value={resendEmail}
                    onChange={handleResendEmailChange}
                    placeholder="Enter your email"
                    className={styles.requestCodeInput}
                    autoComplete="email"
                  />
                  <button
                    className={styles.requestCodeButton}
                    type="button"
                    onClick={handleResendCode}
                    disabled={loading}
                    aria-disabled={loading}
                  >
                    Resend Link
                  </button>
                </div>
                {resendBannerText && (
                  <div
                    className={
                      resendDomainState === 'checking' ? styles.statusChecking :
                      resendDomainState === 'valid' ? styles.statusValid :
                      resendDomainState === 'invalid' ? styles.statusInvalid :
                      styles.statusInfo
                    }
                    aria-live="polite"
                    role="status"
                  >
                    {resendDomainState === 'checking' && <span className={styles.spinner} aria-hidden="true" />}
                    <span>{resendBannerText}</span>
                  </div>
                )}
                {resendMessage && (
                  <div className={styles.statusInfo} role="status" aria-live="polite">
                    <span>{resendMessage}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  )
}
